/**
 * `ProjectStore` over a single `localStorage` blob.
 *
 * The fallback for browsers where IndexedDB is unavailable (Firefox private
 * windows, some embedded webviews, aggressive privacy extensions). Every write
 * rewrites the whole document, which is acceptable at the scale localStorage
 * allows anyway — roughly 5 MB per origin, and we surface `QuotaExceededError`
 * rather than letting a write fail silently.
 */
import { storageKey } from '@/config/branding';
import type { CreateProjectInput, ProjectStore } from './ProjectStore';
import type { ProjectRecord, ProjectSummary } from './types';
import { StorageUnavailableError, isProjectRecord } from './types';
import { asStorageError } from './errors';
import {
  buildDuplicate,
  buildRecord,
  cloneRecord,
  normalizeName,
  nowIso,
  summarize,
} from './records';

const PROJECTS_KEY = storageKey('projects');
const DOCUMENT_VERSION = 1;

interface StoredDocument {
  readonly version: number;
  readonly projects: readonly unknown[];
}

/**
 * Resolved per call rather than captured at module load: tests swap the global
 * in and out, and a browser can revoke access when the user changes cookie
 * settings mid-session.
 */
function requireStorage(): Storage {
  const storage: Storage | undefined = globalThis.localStorage;
  if (!storage) throw new StorageUnavailableError('localStorage is not available.');
  return storage;
}

/** Accepts both the current `{ version, projects }` document and a bare array. */
function extractCandidates(parsed: unknown): readonly unknown[] {
  if (Array.isArray(parsed)) return parsed;
  if (typeof parsed !== 'object' || parsed === null) return [];
  const projects = (parsed as Partial<StoredDocument>).projects;
  return Array.isArray(projects) ? projects : [];
}

/**
 * Reads and validates the blob. Anything unparsable is treated as "no
 * projects" rather than throwing, so a corrupt entry cannot lock the user out
 * of the app; individual malformed records are dropped and the rest survive.
 */
function readAll(storage: Storage): ProjectRecord[] {
  const raw = storage.getItem(PROJECTS_KEY);
  if (raw === null) return [];

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return [];
  }

  return extractCandidates(parsed).filter(isProjectRecord).map(cloneRecord);
}

function writeAll(storage: Storage, records: readonly ProjectRecord[]): void {
  // Not named `document`: this runs in a browser, where that shadows the DOM global.
  const payload: StoredDocument = { version: DOCUMENT_VERSION, projects: records };
  storage.setItem(PROJECTS_KEY, JSON.stringify(payload));
}

export function createLocalStorageStore(): ProjectStore {
  /**
   * Single funnel for storage access so every failure — missing global, denied
   * access, quota — leaves as one of our two error types.
   */
  function withStorage<T>(operation: string, body: (storage: Storage) => T): T {
    try {
      return body(requireStorage());
    } catch (error) {
      throw asStorageError(error, operation);
    }
  }

  async function list(): Promise<ProjectSummary[]> {
    return withStorage('list', (storage) => summarize(readAll(storage)));
  }

  async function get(id: string): Promise<ProjectRecord | null> {
    return withStorage(
      'get',
      (storage) => readAll(storage).find((record) => record.id === id) ?? null,
    );
  }

  async function put(record: ProjectRecord): Promise<void> {
    withStorage('put', (storage) => {
      const records = readAll(storage);
      const next = cloneRecord(record);
      const index = records.findIndex((entry) => entry.id === record.id);
      if (index >= 0) records[index] = next;
      else records.push(next);
      writeAll(storage, records);
    });
  }

  async function create(input: CreateProjectInput): Promise<ProjectRecord> {
    return withStorage('create', (storage) => {
      const records = readAll(storage);
      const record = buildRecord(input);
      records.push(record);
      writeAll(storage, records);
      return record;
    });
  }

  async function rename(id: string, name: string): Promise<ProjectRecord | null> {
    return withStorage('rename', (storage) => {
      const records = readAll(storage);
      const index = records.findIndex((entry) => entry.id === id);
      if (index < 0) return null;
      const updated: ProjectRecord = {
        ...records[index],
        name: normalizeName(name),
        updatedAt: nowIso(),
      };
      records[index] = updated;
      writeAll(storage, records);
      return updated;
    });
  }

  async function duplicate(id: string): Promise<ProjectRecord | null> {
    return withStorage('duplicate', (storage) => {
      const records = readAll(storage);
      const original = records.find((entry) => entry.id === id);
      if (!original) return null;
      const copy = buildDuplicate(
        original,
        records.map((entry) => entry.name),
      );
      records.push(copy);
      writeAll(storage, records);
      return copy;
    });
  }

  async function remove(id: string): Promise<void> {
    withStorage('remove', (storage) => {
      const records = readAll(storage);
      const next = records.filter((entry) => entry.id !== id);
      // Skip the write when nothing matched; a delete should never risk quota.
      if (next.length !== records.length) writeAll(storage, next);
    });
  }

  async function search(query: string): Promise<ProjectSummary[]> {
    return withStorage('search', (storage) => summarize(readAll(storage), query));
  }

  async function clear(): Promise<void> {
    withStorage('clear', (storage) => storage.removeItem(PROJECTS_KEY));
  }

  return { list, get, put, create, rename, duplicate, remove, search, clear, kind: 'localstorage' };
}
